// Resolving a NATIVE Giraffe-authored Custom Calculation (one never typed as
// a gTables formula — authored directly in Giraffe's own Analytics editor,
// or built into the project) into a plain Excel formula the Areas sheet
// can use as a tier-d column, exactly like a gTables `GI.SUMEACH`-declared
// one.
//
// Previously "deliberately not built" (see the comment this replaces in
// `analyticsMeasures.ts`) because Analytics' own field vocabulary
// ("METERS_2__netArea") differs from the bakedSection names this app reads.
// Solved below, confirmed against this project's real measures (see
// README.md's "Confirmed Giraffe SDK facts", 2026-10-24):
// `rawProperty` component ids are often `METERS_<n>__<fieldName>` — the
// prefix just encodes a physical unit dimension and strips cleanly to the
// plain bakedSection field name. Some ids are already unprefixed
// (`hardCost`, `Lease Term`, …) and match directly.
//
// Deliberately NARROW, by instruction: only a measure where EVERY component
// is a `rawProperty` (Analytics calls this "Property") is resolved — a
// `lineItem`/`subtotal`/`site` component anywhere makes the whole measure
// unmappable, full stop, no recursion into what it references. And a
// measure whose formula is just a bare passthrough of its one component
// (`formula === "A"`, e.g. Giraffe's own "GFA", "Residential Units") is
// skipped too — nothing is gained by mirroring a single field with a new
// name when gTables already shows that field directly.
//
// A measure's `formula` is a tiny expression over COMPONENT LETTERS (A, B,
// C, D, skipping E, then F, G, …) in the SAME grammar `data/expression.ts`
// already parses — confirmed live. So the actual per-row FORMULA is built
// exactly like a gTables expression: `toExcelFormula(measure.formula, ref)`,
// treating each letter as an ordinary field-name token (confirmed its
// `cellRef` callback already fires for every bare-name token — no change to
// `expression.ts` needed).
//
// The column HEADER is different from `toExcelFormula`'s output on purpose:
// it is the formula text with each letter replaced by its field name,
// LITERALLY — not re-parsed and re-rendered — so `A*B/(C/100)` becomes
// `saleableArea*netRent/(Yield/100)`, exactly Giraffe's own parenthesisation
// and spacing, not a fully-parenthesised rebuild. `measure.name` (Giraffe's
// own label, e.g. "Retail") is not used as the header at all — the point is
// to show what the number actually IS, the same convention gTables' own
// `GI.SUMEACH`-declared columns already use.

import { toExcelFormula } from '@/data/expression';
import { squashField } from '@/data/entityTable';
import type { GiraffeFeatureFilter, GiraffeMeasure, AnalyticsLayout } from './analyticsMeasures';

/** A, B, C, D, F, G, … — components are named by letter in order, skipping E. */
export const COMPONENT_LETTERS = 'ABCDFGHIJKLMNOPQRSTUVWXYZ';

export interface ResolvedMeasure {
  /** The formula text with letters replaced by field names — the column header. */
  header: string;
  /** Canonical Features field names this column's formula needs. */
  fields: readonly string[];
  /** Builds the Excel formula TEXT (no leading '='), given a column-ref resolver. */
  template: (ref: (field: string) => string) => string;
}

export interface UnmappableMeasure {
  name: string;
  unmappable: string;
}

export type MeasureResolution = ResolvedMeasure | UnmappableMeasure;

export function isResolved(m: MeasureResolution): m is ResolvedMeasure {
  return !('unmappable' in m);
}

/** `METERS_2__netArea` -> `netArea`; `hardCost` passes through unchanged. */
function stripMetersPrefix(id: string): string {
  return id.replace(/^METERS_-?\d+__/, '');
}

/**
 * The formula with each whole-word single uppercase letter replaced by its
 * field name, leaving every operator, paren and space exactly as Giraffe
 * wrote it. A regex rather than a reparse-and-rerender: the header is
 * display text, not something fed back through the expression grammar, so
 * there is nothing to validate — and `\b[A-Z]\b` cannot mis-fire on a
 * function name (`IF`, `AND`, …), since a letter inside one always has a
 * same-case neighbour on at least one side, which word-boundary matching
 * refuses to split.
 */
export function substituteLetters(formula: string, letterToField: ReadonlyMap<string, string>): string {
  return formula.replace(/\b[A-Z]\b/g, (letter) => letterToField.get(letter) ?? letter);
}

/**
 * A single pair of parentheses wrapping the ENTIRE expression reads as noise
 * in a column heading — `(A*B)` and `A*B` name the same number, and only one
 * of them looks like a typo. Stripped repeatedly, so `((A*B))` normalises in
 * one pass too. Checked by depth, not by regex: `(A)+(B)` starts and ends
 * with a paren but the first one closes long before the end, so it is left
 * alone — stripping it would change what the heading says.
 */
function stripWrappingParens(expr: string): string {
  let out = expr.trim();
  while (out.startsWith('(') && out.endsWith(')')) {
    let depth = 0;
    let wraps = true;
    for (let i = 0; i < out.length; i++) {
      if (out[i] === '(') depth += 1;
      else if (out[i] === ')') {
        depth -= 1;
        if (depth === 0 && i !== out.length - 1) {
          wraps = false;
          break;
        }
      }
    }
    if (!wraps) break;
    out = out.slice(1, -1).trim();
  }
  return out;
}

export interface MappedMeasureComponents {
  letterToField: ReadonlyMap<string, string>;
  fields: readonly string[];
}

/**
 * Map every component of `measure` to a Features-sheet field name by letter,
 * or say why it can't be done — the one place both `resolveNativeMeasures`
 * (a Features-sheet column) and the Analytics sheet's Total column (a
 * `GI.SUMEACH` cross-check formula) resolve a measure's components, so the
 * two never disagree about what counts as mappable.
 */
export function mapMeasureComponents(
  measure: GiraffeMeasure,
  knownFields: ReadonlySet<string>
): MappedMeasureComponents | { unmappable: string } {
  if (measure.components.length === 0) return { unmappable: 'has no components' };
  if (measure.components.some((c) => c.type !== 'rawProperty')) {
    return { unmappable: 'has a component that is not a plain Property' };
  }

  const squashToCanonical = new Map<string, string>();
  for (const f of knownFields) if (!squashToCanonical.has(squashField(f))) squashToCanonical.set(squashField(f), f);

  const letterToField = new Map<string, string>();
  const fields: string[] = [];
  for (let i = 0; i < measure.components.length; i++) {
    const letter = COMPONENT_LETTERS[i];
    if (!letter) return { unmappable: 'more than 25 components — ran out of letters' };
    const id = measure.components[i]!.id;
    if (!id) return { unmappable: `component ${letter} has no field id` };
    const stripped = stripMetersPrefix(id);
    if (stripped.startsWith('_')) {
      return { unmappable: `"${stripped}" is engine bookkeeping, not a feature field` };
    }
    const canonical = squashToCanonical.get(squashField(stripped));
    if (!canonical) return { unmappable: `"${stripped}" has no matching Features column` };
    letterToField.set(letter, canonical);
    if (!fields.includes(canonical)) fields.push(canonical);
  }
  return { letterToField, fields };
}

function isUnmappable(m: MappedMeasureComponents | { unmappable: string }): m is { unmappable: string } {
  return 'unmappable' in m;
}

/**
 * Resolve every measure in `measures` (NOT scoped to any one category — the
 * caller decides which of Giraffe's measures count as "custom calculations"
 * worth surfacing) into a Features-sheet column, or say why it can't be one.
 * `knownFields` are the Areas sheet's own canonical field names.
 */
export function resolveNativeMeasures(
  measures: readonly GiraffeMeasure[],
  knownFields: ReadonlySet<string>
): MeasureResolution[] {
  return measures.map((measure): MeasureResolution => {
    if (measure.formula.trim() === 'A') {
      return { name: measure.name, unmappable: 'a plain passthrough of one field — gTables already shows that field' };
    }
    const mapped = mapMeasureComponents(measure, knownFields);
    if (isUnmappable(mapped)) return { name: measure.name, unmappable: mapped.unmappable };
    const { letterToField, fields } = mapped;

    return {
      header: stripWrappingParens(substituteLetters(measure.formula, letterToField)),
      fields,
      template: (ref) => toExcelFormula(measure.formula, (letter) => ref(letterToField.get(letter)!)),
    };
  });
}

/** An item's position in an externally-given order, or "last" when it is not in it. */
export function indexOfOrBig(arr: readonly string[] | undefined, v: string | undefined): number {
  if (!arr || v === undefined) return Number.MAX_SAFE_INTEGER;
  const i = arr.indexOf(v);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * The order Giraffe's own Analytics panel lists these measures in — see
 * `AnalyticsLayout`'s doc comment for where each piece of that order comes
 * from. Flattened to a single global order across every tab (Analytics
 * itself only ever orders within one tab at a time; the Analytics SHEET
 * lists every tab at once, so tab order breaks the first tie and category
 * order the second, exactly as cashflow's own `buildMeasureIdsForTab`
 * does it, just not re-scoped per tab).
 */
export function sortByAnalyticsOrder(measures: readonly GiraffeMeasure[], layout: AnalyticsLayout | null): GiraffeMeasure[] {
  if (!layout) return [...measures];
  const { categories, display } = layout;

  const categoryOrder: string[] = [];
  const seen = new Set<string>();
  const tabs = display.tabOrder ?? Object.keys(display.tabCategoryOrder ?? {});
  for (const tab of tabs) {
    for (const cat of display.tabCategoryOrder?.[tab] ?? []) {
      if (!seen.has(cat)) {
        seen.add(cat);
        categoryOrder.push(cat);
      }
    }
  }
  for (const cat of Object.keys(categories)) {
    if (!seen.has(cat)) {
      seen.add(cat);
      categoryOrder.push(cat);
    }
  }

  const label = (m: GiraffeMeasure): string => `${categories[m.categoryId]?.label ?? ''} - ${m.name}`;

  return [...measures].sort((a, b) => {
    const catDiff = indexOfOrBig(categoryOrder, a.categoryId) - indexOfOrBig(categoryOrder, b.categoryId);
    if (catDiff !== 0) return catDiff;
    const aRow = display.categoryUsageRowOrders?.[a.categoryId]?.__COMBINED;
    const bRow = display.categoryUsageRowOrders?.[b.categoryId]?.__COMBINED;
    const rowDiff = indexOfOrBig(aRow, a.id) - indexOfOrBig(bRow, b.id);
    if (rowDiff !== 0) return rowDiff;
    return label(a).localeCompare(label(b));
  });
}

/** `UsageType` duplicates `usage` under Giraffe's own Analytics spelling — the one alias a feature filter's `property` needs, matching `BAKED_DUPLICATE_FIELDS` in `data/featureTable.ts`. */
const FILTER_FIELD_ALIASES: Record<string, string> = { usagetype: 'usage' };

/**
 * The measure's own `featureFilter`, as the literal `field, value, …` pairs
 * `GI.WHERE` takes — or `undefined` when it cannot be expressed as one:
 * an `or` at the top level, a clause whose operator this has not been taught
 * (confirmed live so far: `is` -> `=`, `is_not` -> `!=`), a multi-value
 * `select` (there is no safe one-clause translation of "A or B"), or a
 * property with no matching Features column. Declining rather than guessing,
 * same policy as `mapMeasureComponents`.
 */
function whereArgsOf(filter: GiraffeFeatureFilter | undefined, knownFields: ReadonlySet<string>): string[] | undefined {
  if (!filter || filter.filters.length === 0) return [];
  if (filter.operator !== 'and') return undefined;

  const squashToCanonical = new Map<string, string>();
  for (const f of knownFields) if (!squashToCanonical.has(squashField(f))) squashToCanonical.set(squashField(f), f);

  const args: string[] = [];
  for (const clause of filter.filters) {
    const op = clause.filter.operator === 'is' ? '=' : clause.filter.operator === 'is_not' ? '!=' : undefined;
    if (!op) return undefined;
    const raw = clause.filter.value;
    const values = Array.isArray(raw) ? raw : [raw];
    if (values.length !== 1) return undefined;
    const value = values[0];
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;
    const aliased = FILTER_FIELD_ALIASES[clause.property.toLowerCase()] ?? clause.property;
    const field = squashToCanonical.get(squashField(aliased));
    if (!field) return undefined;
    args.push(`"${field}${op === '=' ? '' : op}"`, `"${value}"`);
  }
  return args;
}

/**
 * The measure's own total, built as gTables' OWN `GI.SUMEACH`/`GI.WHERE`
 * formula over the Areas sheet — independent of whatever number
 * `getAnalyticsResult()` already computed, so the Analytics sheet's Sum
 * column (the split columns added together) has something OTHER than
 * Giraffe's own engine to check itself against. `undefined` when it can't
 * be built: `mapMeasureComponents` only ever resolves a Property-only
 * measure (same restriction as the Features-sheet resolver, and for the
 * same reason — `GI.SUMEACH` evaluates its expression once per FEATURE, so a
 * `lineItem`/`subtotal`/`site` component, which names something that is not
 * a per-feature field at all, has no expression to write), and only a `SUM`
 * measure is additive enough for a per-feature expression to reproduce in
 * the first place — Giraffe's own AVERAGE/CONSTANT measures are not the sum
 * of anything, and comparing one against `GI.SUMEACH`'s sum would be a
 * built-in false MISMATCH, not a real one.
 */
export function totalFormulaOf(measure: GiraffeMeasure, knownFields: ReadonlySet<string>): string | undefined {
  if (measure.operation !== 'SUM') return undefined;
  const mapped = mapMeasureComponents(measure, knownFields);
  if (isUnmappable(mapped)) return undefined;
  const where = whereArgsOf(measure.featureFilter, knownFields);
  if (where === undefined) return undefined;

  const expression = substituteLetters(measure.formula, mapped.letterToField);
  const args = [`"${expression}"`, ...(where.length > 0 ? [`GI.WHERE(${where.join(', ')})`] : [])];
  return `GI.SUMEACH(${args.join(', ')})`;
}
